/**
 * useMediaHealthMonitor — detects stalled/black video streams and slow networks.
 *
 * Monitors a <video> element using getVideoPlaybackQuality().
 * - totalVideoFrames stop incrementing → 'stalled' (pipeline dead)
 * - droppedVideoFrames / totalVideoFrames > 30% → 'slow_network'
 *
 * Usage:
 *   const { status, resetMonitor } = useMediaHealthMonitor(videoRef, isActive);
 *   // status: 'waiting' | 'healthy' | 'stalled' | 'slow_network'
 */
import { useRef, useEffect, useState, useCallback } from 'react';

const POLL_INTERVAL_MS = 4000;
const STALL_THRESHOLD = 3;       // 3 stalls × 4s = 12s to detect
const INITIAL_GRACE_MS = 10000;  // Don't check for first 10s
const DROP_RATIO_THRESHOLD = 0.30; // 30% frame drops = slow network
const DROP_CHECK_WINDOW = 5;     // N consecutive polls above threshold (5 × 4s = 20s)

export default function useMediaHealthMonitor(
  videoRef: React.RefObject<HTMLVideoElement>,
  active: boolean,
): { status: 'waiting' | 'healthy' | 'stalled' | 'slow_network'; resetMonitor: () => void } {
  const [status, setStatus] = useState<'waiting' | 'healthy' | 'stalled' | 'slow_network'>('waiting');
  const prevFramesRef = useRef(0);
  const prevDroppedRef = useRef(0);
  const stallCountRef = useRef(0);
  const dropCountRef = useRef(0);
  const activatedAtRef = useRef(0);

  const resetMonitor = useCallback(() => {
    prevFramesRef.current = 0;
    prevDroppedRef.current = 0;
    stallCountRef.current = 0;
    dropCountRef.current = 0;
    activatedAtRef.current = Date.now();
    setStatus('waiting');
  }, []);

  useEffect(() => {
    if (active) {
      activatedAtRef.current = Date.now();
      prevFramesRef.current = 0;
      prevDroppedRef.current = 0;
      stallCountRef.current = 0;
      dropCountRef.current = 0;
      setStatus('waiting');
    } else {
      setStatus('waiting');
    }
  }, [active]);

  useEffect(() => {
    if (!active) return;

    const interval = setInterval(() => {
      const video = videoRef.current;
      if (!video) return;
      if (Date.now() - activatedAtRef.current < INITIAL_GRACE_MS) return;

      let decodedFrames = 0;
      let droppedFrames = 0;
      if (video.getVideoPlaybackQuality) {
        const q = video.getVideoPlaybackQuality();
        decodedFrames = q.totalVideoFrames;
        droppedFrames = q.droppedVideoFrames;
      } else if ((video as any).webkitDecodedFrameCount !== undefined) {
        decodedFrames = (video as any).webkitDecodedFrameCount;
        droppedFrames = (video as any).webkitDroppedFrameCount || 0;
      } else if (video.videoWidth > 0 && video.currentTime > 0 && !video.paused) {
        decodedFrames = Math.floor(video.currentTime * 30);
      }

      // Tab backgrounded: browsers throttle/pause video decode, so the frame counter
      // freezes. That's NOT a stall — counting it blanks a healthy stream and forces a
      // needless resubscribe the moment the user returns. Keep the baseline fresh and bail.
      if (typeof document !== 'undefined' && document.hidden) {
        prevFramesRef.current = decodedFrames;
        prevDroppedRef.current = droppedFrames;
        stallCountRef.current = 0;
        return;
      }

      const prev = prevFramesRef.current;

      // Stall detection
      if (decodedFrames === 0 && prev === 0) {
        stallCountRef.current++;
        if (stallCountRef.current >= STALL_THRESHOLD) setStatus('stalled');
        prevFramesRef.current = decodedFrames;
        prevDroppedRef.current = droppedFrames;
        return;
      }

      if (decodedFrames <= prev) {
        stallCountRef.current++;
        if (stallCountRef.current >= STALL_THRESHOLD) setStatus('stalled');
      } else {
        stallCountRef.current = 0;

        // Frame drop detection (only when stream is progressing)
        const deltaFrames = decodedFrames - prev;
        const deltaDropped = droppedFrames - prevDroppedRef.current;
        if (deltaFrames > 0 && deltaDropped / deltaFrames > DROP_RATIO_THRESHOLD) {
          dropCountRef.current++;
          if (dropCountRef.current >= DROP_CHECK_WINDOW) {
            setStatus('slow_network');
          } else {
            setStatus('healthy');
          }
        } else {
          dropCountRef.current = 0;
          setStatus('healthy');
        }
      }

      prevFramesRef.current = decodedFrames;
      prevDroppedRef.current = droppedFrames;
    }, POLL_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [active, videoRef]);

  return { status, resetMonitor };
}
